/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import React, { useContext } from 'react';
import globalStyle from '../../../../global.scss?inline';
import style from './Consciousness.scss?inline';
import { FormControl } from '@mui/material';
import DiviFieldCard from '../../../customs/DiviFieldCard/DiviFieldCard';
import CheckboxList from '../../../customs/checkboxList/CheckboxList';
import {
  ConsciousnessKeys,
  getConsciousnessTupelsFor,
  consciousnessRadioKeys,
  getRadioValue,
} from '../../../../models/consciousness';
import { InputState } from '../../../../backendModels/general.model';
import { useCSS } from '../../../../provider/CSSProvider';
import { CardId } from '../../../../models/diviCard';
import { NodeType } from '../../../../backendModels/report.model';
import { ReportsAPIContext } from '../../../../provider/ReportsAPIProvider';
import { defaultRecords } from '../../../../DefaultRecords';
import _ from 'lodash';
import RadioList from '../../../customs/radioList/RadioList';
import { useFreshCallback } from '../../../../utils/hooks';

const consciousnessRadioItems: [ConsciousnessKeys, string][] = getConsciousnessTupelsFor([
  ConsciousnessKeys.clouded,
  ConsciousnessKeys.unconscious,
]);

const consciousnessCheckboxItems: [ConsciousnessKeys, string][] = getConsciousnessTupelsFor([
  ConsciousnessKeys.reactionToSpeech,
  ConsciousnessKeys.reactionToPainStimulus,
  ConsciousnessKeys.narcotized,
]);

interface ConsciousnessProps {
  nodeType: NodeType;
}

function Consciousness({ nodeType }: ConsciousnessProps) {
  useCSS(style);
  useCSS(globalStyle);

  const reportAPI = useContext(ReportsAPIContext);
  const record = reportAPI.findRecordOrDefault(defaultRecords.consciousness.type, nodeType);

  const inputState = record.inputState;
  const isInputStateEntered = inputState === InputState.ENTERED;
  const symptoms = (isInputStateEntered ? (record.symptoms ?? []) : []) as ConsciousnessKeys[];

  const handleCheckboxChange = (values: ConsciousnessKeys[]) => {
    reportAPI.adaptRecord(defaultRecords.consciousness.type, nodeType, (draft, deleteRecord) => {
      if (values.length > 0) {
        return { ...draft, inputState: InputState.ENTERED, symptoms: values };
      } else {
        deleteRecord();
      }
    });
  };

  const handleRadioChange = (value: string | null) => {
    reportAPI.adaptRecord(defaultRecords.consciousness.type, nodeType, (draft, deleteRecord) => {
      const symptomsWithoutRadioKeys = _.difference(symptoms, consciousnessRadioKeys);
      if (value === ConsciousnessKeys.woke) {
        return { id: draft.id, type: draft.type, inputState: InputState.NORMAL };
      } else if (value == null && symptomsWithoutRadioKeys.length === 0) {
        deleteRecord();
      } else {
        return {
          ...draft,
          inputState: InputState.ENTERED,
          symptoms: [...symptomsWithoutRadioKeys, ...(value != null ? [value] : [])],
        };
      }
    });
  };

  const setToNormal = useFreshCallback(() => {
    reportAPI.adaptRecord(defaultRecords.consciousness.type, nodeType, (draft) => ({
      id: draft.id,
      type: draft.type,
      inputState: InputState.NORMAL,
    }));
  });

  const setToUnknown = useFreshCallback(() => {
    reportAPI.adaptRecord(defaultRecords.consciousness.type, nodeType, (draft) => ({
      id: draft.id,
      type: draft.type,
      inputState: InputState.UNKNOWN,
    }));
  });

  return (
    <DiviFieldCard
      cardType={CardId.Consciousness}
      onNormalStateButtonClicked={setToNormal}
      normalStateButtonLabel='wach'
      onUnknownStateButtonClicked={setToUnknown}
      recordType={defaultRecords.consciousness.type}
      nodeType={nodeType}>
      <div id='consciousness-area'>
        <div className='symptoms-list row'>
          <FormControl>
            <RadioList
              items={consciousnessRadioItems}
              name='consciousness-group'
              value={getRadioValue(record)}
              onChange={handleRadioChange}
            />
          </FormControl>
          <CheckboxList
            items={consciousnessCheckboxItems}
            selectedValues={symptoms}
            onValuesChange={handleCheckboxChange}
          />
        </div>
      </div>
    </DiviFieldCard>
  );
}

export default Consciousness;
